/*
 * Maison Avéla — header layout.
 *
 * Horizon locks the logo and the account/search/cart icons to the top row;
 * only the menu can be moved down. This puts all three on the same lower
 * row, so the header reads: language above a hairline, then menu, name and
 * icons together beneath it.
 *
 * It moves the existing nodes rather than rendering copies, so the cart
 * and search keep their event bindings. On phones the lower row is hidden
 * by the theme, so everything is moved back up and the header behaves
 * exactly as Horizon intends.
 */
(function () {
  'use strict';

  var COLUMNS = ['left', 'center', 'right'];

  function columnOf(parent, name) {
    if (!parent) return null;
    var children = parent.children;
    for (var i = 0; i < children.length; i++) {
      if (children[i].classList.contains('header__column--' + name)) return children[i];
    }
    return null;
  }

  function apply() {
    var comp = document.querySelector('#header-component');
    if (!comp) return;

    var topRow = comp.querySelector('.header__row--top');
    var bottomRow = comp.querySelector('.header__row--bottom');
    if (!topRow || !bottomRow) return;

    var topColumns = topRow.querySelector('.header__columns');
    var bottomColumns = bottomRow.querySelector('.header__columns');
    if (!topColumns || !bottomColumns) return;

    var bottomVisible = window.getComputedStyle(bottomRow).display !== 'none';

    if (bottomVisible) {
      ['center', 'right'].forEach(function (name) {
        var column = columnOf(topColumns, name);
        if (!column) return;
        column.setAttribute('data-ma-moved', '');
        bottomColumns.appendChild(column);
      });
    } else {
      var moved = comp.querySelectorAll('[data-ma-moved]');
      if (!moved.length) return;
      moved.forEach(function (column) {
        column.removeAttribute('data-ma-moved');
        topColumns.appendChild(column);
      });
    }

    // Keep the visual order intact after any move.
    COLUMNS.forEach(function (name) {
      var column = columnOf(bottomVisible ? bottomColumns : topColumns, name);
      if (column) column.parentNode.appendChild(column);
    });
  }

  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      apply();
    });
  }

  function boot() {
    apply();
    // The header measures its own height on load; let it measure the new one.
    window.setTimeout(function () {
      window.dispatchEvent(new Event('resize'));
    }, 0);

    var comp = document.querySelector('#header-component');
    if (comp && window.MutationObserver) {
      // Horizon flips between menu and drawer modes on touch and on resize.
      new MutationObserver(schedule).observe(comp, {
        attributes: true,
        attributeFilter: ['data-menu-style'],
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('resize', schedule);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('shopify:section:load', schedule);
})();

/*
 * Maison Avéla — successful add-to-cart acknowledgement.
 *
 * Lives in this file because it is the theme's one globally deferred,
 * header-scoped script (loaded from snippets/stylesheets.liquid), so the
 * listener is registered on every page long before a customer can reach a
 * BUY button, and no new file has to be loaded to get it there.
 *
 * It adds no cart request and intercepts none. It only listens for the
 * storefront's own 'shopify:cart:lines-update' event, which every add
 * surface in this theme already goes through — the homepage/PDP BUY pills
 * and the cart-drawer upsell via window.Shopify.actions.updateCart, and
 * the product page / quick-add forms via the theme's own product form.
 * That event carries the action ('add' | 'remove' | 'update') and a
 * promise that resolves with the resulting cart, so the acknowledgement
 * fires only for an add that actually succeeded: a removal, a quantity
 * change, opening the cart, a page load or restore, and a request that
 * fails, is cancelled or comes back with userErrors all leave it silent.
 *
 * The visual side is entirely CSS (transform and opacity only) and lives
 * in snippets/header-actions.liquid, next to the cart icon it animates.
 * Nothing about the cart request, the drawer, the count logic, the
 * upsell, Econt or checkout is touched.
 */
(function () {
  'use strict';

  var CLASS = 'ma-cart-added';
  // Comfortably longer than the CSS animation; a safety net only, so the
  // class can never be left behind if animationend is never delivered.
  var CLEANUP_MS = 700;

  function acknowledge() {
    // Reduced motion: the count still updates as usual, nothing animates.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var icons = document.querySelectorAll('cart-icon');

    for (var i = 0; i < icons.length; i++) {
      (function (icon) {
        // Restart cleanly when a second item is added mid-animation.
        icon.classList.remove(CLASS);
        void icon.offsetWidth;
        icon.classList.add(CLASS);

        window.clearTimeout(icon.maCartAddedTimer);
        icon.maCartAddedTimer = window.setTimeout(function () {
          icon.classList.remove(CLASS);
        }, CLEANUP_MS);
      })(icons[i]);
    }
  }

  /*
   * Opens the existing cart drawer after a successful add — reusing the
   * exact element/method assets/standard-actions-override.js already
   * calls for Shopify.actions.openCart (`document.querySelector(
   * 'theme-drawer#cart-drawer').open()`). No new drawer, no DOM class
   * manipulation, no second overlay: this is the same public .open()
   * ThemeDrawer exposes, which already handles the modal/non-modal
   * split, focus trap, and scroll lock for both breakpoints. Calling it
   * while the drawer is already open (e.g. adding a cart-upsell item
   * from inside the open drawer) is a harmless no-op re-front, not a
   * second drawer. If cart_type isn't 'drawer', the element doesn't
   * exist and this silently does nothing — no fallback navigation, since
   * unlike the manual cart-icon click this is a passive enhancement.
   */
  function openCartDrawer() {
    var drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (event.action !== 'add') return;

    var promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise.then(
      function (result) {
        if (result && result.userErrors && result.userErrors.length) return;
        acknowledge();
        openCartDrawer();
      },
      function () {
        // Rejected: the add failed, errored, or was aborted. Stay silent.
      }
    );
  });
})();
